import cn from 'classnames';
import { formatDistanceToNow, isDate, isValid, parseISO } from 'date-fns';
import type { NotificationList } from 'models';
import { useNavigate } from 'react-router';
import { Flex, Text } from 'ui';
import { useAdminDashboardRedirectMutation } from 'features/admin-dashboard';
import { useReadNotificationMutation } from '../queries';
import { useNotificationIcon } from './hooks';
import styles from './notification.module.css';

type P = {
  notification: NotificationList;
};

const Notification = ({ notification }: P) => {
  const navigate = useNavigate();
  const { mutate: redirectToAdminDashboard } = useAdminDashboardRedirectMutation();
  const { mutate: readNotification } = useReadNotificationMutation();
  const { getIcon } = useNotificationIcon();

  const parsedDate = parseISO(notification.createdAt);
  const isValidDate = isDate(parsedDate) && isValid(parsedDate);

  const handleClick = async () => {
    if (notification.url.includes(String(import.meta.env.VITE_APP_ADMIN_DASHBOARD_URL))) {
      const parsedUrl = new URL(notification.url);
      redirectToAdminDashboard({ referrer: parsedUrl.pathname + parsedUrl.search });
    } else {
      await navigate(notification.path);
    }
    readNotification(notification.id);
  };

  return (
    <Flex
      align="center"
      className={cn(styles.notification, {
        [styles.unread ?? '']: notification.status !== 'read',
      })}
      gap="ms"
      onClick={async () => {
        await handleClick();
      }}
    >
      <Flex align="center" className={styles.icon} justify="center">
        {getIcon({ iconType: notification.icon, isRead: notification.status === 'read' })}
      </Flex>

      <Flex className={styles.content} flex="1" gap="xxs" vertical>
        <Text weight="bold">{notification.name}</Text>
        <Text>{notification.content}</Text>
        <Text size="small">
          {isValidDate ? formatDistanceToNow(parsedDate) : null} <Text size="small">ago</Text>
        </Text>
      </Flex>
    </Flex>
  );
};

export default Notification;
